import React from 'react';
import { 
  Box, 
  Flex,
  Heading, 
  Text, 
  Button, 
  VStack,
  Container,
  SimpleGrid,
  Stack
} from '@chakra-ui/react';
import { FaChartPie, FaLightbulb, FaUser, FaBookOpen, FaChalkboardUser, FaGraduationCap } from 'react-icons/fa6';
import { IconType } from 'react-icons';
import { Link as RouterLink, useLocation } from 'react-router-dom';

interface FeatureProps {
  title: string;
  text: string;
  icon: IconType;
}

const Feature = ({ title, text, icon }: FeatureProps) => {
  return (
    <Stack align={'center'} textAlign={'center'}>
      <Flex
        w={16}
        h={16}
        alignItems={'center'}
        justifyContent={'center'}
        color={'white'}
        rounded={'full'}
        bg={'primary.500'}
        boxShadow={'0 0 0 6px var(--chakra-colors-accent-100)'}
        mb={3}
      >
        {/* Renderiza el icono como JSX directamente */}
        {icon && icon({ size: 40 })}
      </Flex>
      <Text fontWeight={600}>{title}</Text>
      <Text color={'gray.600'}>{text}</Text>
    </Stack>
  );
};

const Landing: React.FC = () => {
  const location = useLocation();
  const testRoute = {
    pathname: '/test',
    search: location.search,
  };

  return (
    <Box>
      {/* Hero Section */}
      <Container maxW={'7xl'}>
        <Stack
          align={'center'}
          spacing={{ base: 8, md: 10 }}
          py={{ base: 20, md: 28 }}
          direction={{ base: 'column', md: 'row' }}>
          <Stack flex={1} spacing={{ base: 5, md: 10 }}>
            <Heading
              lineHeight={1.1}
              fontWeight={600}
              fontSize={{ base: '3xl', sm: '4xl', lg: '6xl' }}>
              <Text
                as={'span'}
                position={'relative'}
                color={'primary.500'}>
                Conoce cómo aprende
              </Text>
              <br />
              <Text as={'span'} color={'accent.600'}>
                cada alumno
              </Text>
            </Heading>
            <Text color={'gray.500'}>
              Un cuestionario de 80 preguntas que ayuda a identificar el perfil de aprendizaje y las motivaciones del alumno. Los resultados alimentan la plataforma Aididactic para que docentes y tutores personalicen el acompañamiento educativo.
            </Text>
            <Stack
              spacing={{ base: 4, sm: 6 }}
              direction={{ base: 'column', sm: 'row' }}>
              <Button
                as={RouterLink}
                to={testRoute}
                rounded={'full'}
                size={'lg'}
                fontWeight={'normal'}
                px={6}
                colorScheme={'blue'}
                bg={'primary.500'}
                _hover={{ bg: 'primary.600' }}>
                Empezar cuestionario
              </Button>
              <Button
                as={'a'}
                href={'#ventajas'}
                rounded={'full'}
                size={'lg'}
                fontWeight={'normal'}
                px={6}
                leftIcon={<span>{FaLightbulb && FaLightbulb({ size: 20 })}</span>}>
                Cómo funciona
              </Button>
            </Stack>
          </Stack>
          <Flex
            flex={1}
            justifyContent={'center'}
            alignItems={'center'}
            position={'relative'}
            w={'full'}>
            <Box
              position={'relative'}
              minH={{ base: 'auto', md: '400px' }}
              rounded={'2xl'}
              boxShadow={'2xl'}
              width={'full'}
              p={{ base: 6, md: 10 }}
              bgGradient={'linear(to-br, primary.500, primary.700)'}
              color={'white'}
              overflow={'hidden'}>
              <Box
                position={'absolute'}
                top={'-60px'}
                right={'-60px'}
                w={'220px'}
                h={'220px'}
                rounded={'full'}
                bg={'accent.500'}
                opacity={0.25}
              />
              <VStack align={'stretch'} spacing={4} position={'relative'} justify={'center'} minH={{ md: '320px' }}>
                {[
                  { icon: FaBookOpen, title: 'Cómo aprende', text: 'Estilo de trabajo y motivaciones' },
                  { icon: FaChalkboardUser, title: 'Cómo acompañar', text: 'Pautas para docentes y tutores' },
                  { icon: FaGraduationCap, title: 'Cómo avanza', text: 'Seguimiento en el panel del alumno' },
                ].map(({ icon, title, text }) => (
                  <Flex
                    key={title}
                    alignItems={'center'}
                    gap={4}
                    p={4}
                    rounded={'xl'}
                    bg={'whiteAlpha.200'}
                    border={'1px solid'}
                    borderColor={'whiteAlpha.300'}>
                    <Flex
                      w={12}
                      h={12}
                      flexShrink={0}
                      alignItems={'center'}
                      justifyContent={'center'}
                      rounded={'lg'}
                      bg={'accent.500'}>
                      {icon({ size: 24 })}
                    </Flex>
                    <Box>
                      <Text fontWeight={700}>{title}</Text>
                      <Text fontSize={'sm'} color={'whiteAlpha.800'}>{text}</Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            </Box>
          </Flex>
        </Stack>
      </Container>

      {/* Feature Section */}
      <Box id={'ventajas'} py={16} bg={'white'} border={'1px solid'} borderColor={'primary.100'} rounded={'2xl'}>
        <Container maxW={'7xl'}>
          <VStack spacing={2} textAlign={'center'} mb={12}>
            <Heading as={'h2'} fontSize={'3xl'} color={'primary.500'}>
              ¿Para qué sirve este cuestionario?
            </Heading>
            <Text color={'gray.500'}>
              Una herramienta de apoyo para la orientación y la personalización educativa
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            <Feature
              icon={FaChartPie}
              title={'Perfil de aprendizaje'}
              text={'Identifica fortalezas, motivaciones y estilos de trabajo para entender mejor cómo aprende cada alumno.'}
            />
            <Feature
              icon={FaLightbulb}
              title={'Recomendaciones para el aula'}
              text={'Pautas prácticas para docentes y tutores sobre cómo motivar, comunicar y acompañar a cada perfil.'}
            />
            <Feature
              icon={FaUser}
              text={'El resultado se integra en el panel del alumno para apoyar la tutoría y la planificación de actividades.'}
              title={'Seguimiento en Aididactic'}
            />
          </SimpleGrid>
        </Container>
      </Box>

      {/* Call to action */}
      <Box py={16}>
        <Container maxW={'7xl'}>
          <VStack spacing={2} textAlign={'center'}>
            <Heading as={'h2'} fontSize={'3xl'}>
              ¿Listo para empezar?
            </Heading>
            <Text color={'gray.500'} px={8} py={4} maxW={'2xl'}>
              Responde con sinceridad: no hay respuestas correctas o incorrectas. Te llevará unos 15 minutos.
            </Text>
            <Button
              as={RouterLink}
              to={testRoute}
              rounded={'full'}
              size={'lg'}
              fontWeight={'normal'}
              px={6}
              mt={4}
              colorScheme={'blue'}
              bg={'primary.500'}
              _hover={{ bg: 'primary.600' }}>
              Empezar ahora
            </Button>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
